import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ChatChannelListResult, ChatChannelResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Must match GLOBAL_CHANNEL_ID in @fias/db-types */
const GLOBAL_CHANNEL_ID = 'cch_global_fias_community';

interface BumpLastMessage {
  content: string;
  senderId: string;
  senderName: string | null;
  createdAt: string;
}

interface UseChatChannelsReturn {
  channels: ChatChannelResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  bumpChannel: (channelId: string, lastMessage: BumpLastMessage) => void;
  createChannel: (input: {
    channelType: 'group' | 'guild';
    name: string;
    description?: string;
  }) => Promise<ChatChannelResponse | null>;
  /** `guildId` opens the guild-scoped DM; omitting it opens the personal one. */
  openDirectMessage: (
    targetUserId: string,
    guildId?: string,
  ) => Promise<ChatChannelResponse | null>;
}

export interface UseChatChannelsOptions {
  /**
   * Auto-join the global community channel when the user isn't a member yet.
   * Defaults to true (the chat arche's behavior). Embedded surfaces that merely
   * render one channel (e.g. the guild page's EmbeddedChannelChat) pass false —
   * viewing a guild must never silently enroll the user in the community channel.
   */
  autoJoinGlobal?: boolean;
}

export function useChatChannels({
  autoJoinGlobal = true,
}: UseChatChannelsOptions = {}): UseChatChannelsReturn {
  const queryClient = useQueryClient();
  const channelsQueryKey = queryKeys.chat.channels();
  const hasAutoJoinedGlobal = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: channelsQueryKey,
    queryFn: () => api.listChatChannels(),
  });

  // Auto-join global channel side effect
  useEffect(() => {
    if (!autoJoinGlobal || !data || hasAutoJoinedGlobal.current) return;
    hasAutoJoinedGlobal.current = true;

    const hasGlobal = data.channels.some((c) => c.channelId === GLOBAL_CHANNEL_ID);
    if (hasGlobal) return;

    api
      .joinChatChannel(GLOBAL_CHANNEL_ID)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: channelsQueryKey });
      })
      .catch(() => {
        // Global channel may not exist yet (migration not run) — ignore
      });
  }, [autoJoinGlobal, data, queryClient, channelsQueryKey]);

  const bumpChannel = useCallback(
    (channelId: string, lastMessage: BumpLastMessage) => {
      queryClient.setQueryData<ChatChannelListResult>(channelsQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          channels: old.channels.map((ch) =>
            ch.channelId === channelId ? { ...ch, lastMessage } : ch,
          ),
        };
      });
    },
    [queryClient, channelsQueryKey],
  );

  const createChannelMut = useMutation({
    mutationFn: (input: { channelType: 'group' | 'guild'; name: string; description?: string }) =>
      api.createChatChannel(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });

  const openDMMut = useMutation({
    mutationFn: ({ targetUserId, guildId }: { targetUserId: string; guildId?: string }) =>
      api.getOrCreateDirectChannel(targetUserId, guildId ? { guildId } : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });

  return {
    channels: data?.channels ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load channels' : null,
    refresh: () => {
      refetch();
    },
    bumpChannel,
    createChannel: async (input) => {
      try {
        const result = await createChannelMut.mutateAsync(input);
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create channel';
        throw new Error(message);
      }
    },
    openDirectMessage: async (targetUserId: string, guildId?: string) => {
      try {
        const result = await openDMMut.mutateAsync({ targetUserId, guildId });
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to open direct message';
        throw new Error(message);
      }
    },
  };
}
